import { useState } from "react";
import { Facebook, Instagram } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_villa-surveillance/artifacts/27e1elgx_Logo.png";

const navLinks = [
  { label: "À propos", href: "#apropos" },
  { label: "Nos Services", href: "#offres" },
  { label: "Contact", href: "#coordonnees" },
];

export default function Footer() {
  const [showCGV, setShowCGV] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <footer data-testid="footer" className="bg-[#2E3A34] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 mb-16">
            {/* Logo + Slogan */}
            <div className="text-center md:text-left">
              <div className="h-48 w-64 overflow-hidden flex items-center justify-center mx-auto md:mx-0 mb-6">
                <img src={LOGO_URL} alt="AŪRA HOME CARE" className="h-[220%] w-auto max-w-none" />
              </div>
              <p
                className="text-white/60 text-lg italic leading-relaxed md:max-w-[16rem] mx-auto md:mx-0 md:text-center"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                La sérénité de votre maison,<br />même en votre absence
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4
                className="text-white text-sm tracking-[0.2em] uppercase mb-6"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                Navigation
              </h4>
              <ul className="space-y-3">
                {navLinks.map((link) => (
                  <li key={link.href}>
                    <button
                      onClick={() => scrollTo(link.href)}
                      data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                      className="text-white/60 hover:text-[#b39a73] transition-colors text-lg"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Social + Legal */}
            <div>
              <h4
                className="text-white text-sm tracking-[0.2em] uppercase mb-6"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                Suivez-nous
              </h4>
              <div className="flex gap-4 mb-8">
                <a
                  href="https://www.facebook.com/share/1LaP1vAmCh/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-facebook"
                  className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:border-[#b39a73] hover:text-[#b39a73] transition-all"
                >
                  <Facebook className="h-4 w-4" strokeWidth={1.5} />
                </a>
                <a
                  href="https://www.instagram.com/aurahomecare.tahiti?igsh=MWYwcjNpYnNlMzEzNg=="
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram"
                  className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:border-[#b39a73] hover:text-[#b39a73] transition-all"
                >
                  <Instagram className="h-4 w-4" strokeWidth={1.5} />
                </a>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => setShowPrivacy(true)}
                  data-testid="footer-privacy"
                  className="block text-white/40 hover:text-[#b39a73] text-sm transition-colors"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Politique de confidentialité
                </button>
                <button
                  onClick={() => setShowMentions(true)}
                  data-testid="footer-mentions"
                  className="block text-white/40 hover:text-[#b39a73] text-sm transition-colors"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Mentions légales
                </button>
                <button
                  onClick={() => setShowCGV(true)}
                  data-testid="footer-cgv"
                  className="block text-white/40 hover:text-[#b39a73] text-sm transition-colors"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Conditions Générales de Vente
                </button>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p
              className="text-white/30 text-sm text-center md:text-left"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              &copy; 2025 AŪRA HOME CARE — Tous droits réservés
            </p>
            <p
              className="text-white/20 text-xs text-center md:text-right"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              AŪRA HOME CARE est une entreprise immatriculée en Polynésie française.
            </p>
          </div>
        </div>
      </footer>

      {/* CGV Modal */}
      <Dialog open={showCGV} onOpenChange={setShowCGV}>
        <DialogContent className="max-w-3xl max-h-[85vh] bg-[#FAF8F5] border-[#b39a73]/30">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#2E3A34]" style={{ fontFamily: "'Cinzel', serif" }}>
              Conditions Générales de Vente
            </DialogTitle>
            <DialogDescription className="text-[#4E4A45]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              AŪRA HOME CARE — La sérénité de votre maison, même en votre absence
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <CGVContent />
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Mentions Légales Modal */}
      <Dialog open={showMentions} onOpenChange={setShowMentions}>
        <DialogContent className="max-w-3xl max-h-[85vh] bg-[#FAF8F5] border-[#b39a73]/30">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#2E3A34]" style={{ fontFamily: "'Cinzel', serif" }}>
              Mentions Légales
            </DialogTitle>
            <DialogDescription className="text-[#4E4A45]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Informations légales obligatoires
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <MentionsContent />
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Privacy Modal */}
      <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
        <DialogContent className="max-w-3xl max-h-[85vh] bg-[#FAF8F5] border-[#b39a73]/30">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#2E3A34]" style={{ fontFamily: "'Cinzel', serif" }}>
              Politique de Confidentialité
            </DialogTitle>
            <DialogDescription className="text-[#4E4A45]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Protection de vos données personnelles
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <PrivacyContent />
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}

function LegalSection({ title, children }) {
  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-[#2E3A34] mb-3" style={{ fontFamily: "'Cinzel', serif", fontSize: "0.95rem" }}>
        {title}
      </h3>
      <div className="text-[#4E4A45] text-base leading-relaxed space-y-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        {children}
      </div>
    </div>
  );
}

function CGVContent() {
  return (
    <div className="space-y-2">
      <LegalSection title="Article 1 – Objet">
        <p>Les présentes Conditions Générales de Vente (ci-après les « CGV ») ont pour objet de définir les conditions dans lesquelles AŪRA HOME CARE fournit au Client ses prestations de surveillance, de gestion, de coordination et d'assistance relatives à des propriétés privées situées en Polynésie française.</p>
        <p>Elles ont vocation à s'appliquer à l'ensemble des prestations proposées par AŪRA HOME CARE, en articulation avec le contrat de prestation, le devis accepté et, le cas échéant, toute annexe ou validation écrite convenue entre les parties.</p>
      </LegalSection>
      <LegalSection title="Article 2 – Acceptation des CGV">
        <p>Les présentes CGV sont systématiquement communiquées au Client préalablement à toute validation de prestation. Toute signature du contrat, validation d'un devis, confirmation écrite par email, message ou tout autre moyen de communication écrit, ainsi que tout commencement d'exécution d'une prestation, emporte acceptation pleine et entière des présentes CGV, sans réserve.</p>
      </LegalSection>
      <LegalSection title="Article 3 – Champ d'application">
        <p>Les présentes CGV s'appliquent à l'ensemble des prestations proposées par AŪRA HOME CARE, sauf conditions particulières expressément convenues par écrit entre les parties.</p>
      </LegalSection>
      <LegalSection title="Article 4 – Description des prestations">
        <p>AŪRA HOME CARE propose des prestations de surveillance, de gestion, de coordination et d'assistance relatives à des propriétés privées pendant l'absence du Client. Les prestations sont définies au cas par cas en fonction de la formule choisie, de la fréquence des interventions, des caractéristiques du bien et des demandes spécifiques du Client.</p>
      </LegalSection>
      <LegalSection title="Article 5 – Prise de commande et devis">
        <p>Toute prestation peut donner lieu à l'établissement préalable d'un devis. Le devis est valable pour la durée qui y est indiquée. À défaut de mention spécifique, sa durée de validité est de trente (30) jours à compter de sa date d'émission.</p>
      </LegalSection>
      <LegalSection title="Article 6 – Tarifs">
        <p>Les tarifs des prestations sont exprimés en francs pacifiques (XPF). Ils sont définis soit sur la base d'un prix par passage, soit sous forme de forfait hebdomadaire ou mensuel, soit selon un devis personnalisé.</p>
      </LegalSection>
      <LegalSection title="Article 7 – Frais supplémentaires">
        <p>Certaines prestations peuvent donner lieu à des frais supplémentaires, notamment la coordination d'intervenants externes, les achats réalisés pour le compte du Client, les déplacements spécifiques ou urgents.</p>
      </LegalSection>
      <LegalSection title="Article 8 – Modalités de paiement">
        <p>Les factures sont payables dans un délai de sept (7) jours à compter de leur date d'émission. Les paiements peuvent être effectués par virement bancaire ou tout autre moyen convenu entre les parties.</p>
      </LegalSection>
      <LegalSection title="Article 9 – Modification, report ou annulation">
        <p>Toute demande de modification, de report ou d'annulation doit être communiquée par écrit dans les meilleurs délais. En cas d'annulation plus de 48h avant : aucune pénalité. Entre 24h et 48h : jusqu'à 50% facturé. Moins de 24h : facturée en totalité.</p>
      </LegalSection>
      <LegalSection title="Article 10 – Obligations du Client">
        <p>Le Client s'engage à fournir l'ensemble des informations nécessaires : moyens d'accès au bien, informations relatives aux équipements, consignes spécifiques, informations relatives aux animaux et coordonnées de toute personne utile en cas d'urgence.</p>
      </LegalSection>
      <LegalSection title="Article 13 – Limitation de responsabilité">
        <p>Les prestations relèvent d'une obligation de moyens et non d'une obligation de résultat. La responsabilité d'AŪRA HOME CARE est limitée au montant total des sommes effectivement perçues au titre de la prestation concernée.</p>
      </LegalSection>
      <LegalSection title="Article 18 – Droit applicable et litiges">
        <p>Les présentes CGV sont régies par le droit applicable en Polynésie française. En cas de litige, les parties s'engagent à rechercher une solution amiable en priorité. Le médiateur compétent est : Association des Médiateurs Diplômés de Polynésie Française (AMDPF).</p>
      </LegalSection>
    </div>
  );
}

function MentionsContent() {
  return (
    <div className="space-y-2">
      <LegalSection title="Éditeur du site">
        <p><strong>AŪRA HOME CARE</strong></p>
        <p>Siège social : Punaauia, Tahiti — Polynésie française</p>
        <p>N° Patente (n° Tahiti) : G75550</p>
        <p>Email : aurahomecaretahiti@gmail.com</p>
        <p>Téléphone : (+689) 89 51 07 65</p>
      </LegalSection>
      <LegalSection title="Responsable de publication">
        <p>AŪRA HOME CARE</p>
      </LegalSection>
      <LegalSection title="Hébergement">
        <p>Le site est hébergé par un prestataire professionnel assurant la sécurité et la disponibilité du service.</p>
      </LegalSection>
      <LegalSection title="Propriété intellectuelle">
        <p>L'ensemble du contenu de ce site (textes, images, logos, graphismes) est la propriété exclusive d'AŪRA HOME CARE ou de ses partenaires. Toute reproduction, même partielle, est interdite sans autorisation préalable écrite.</p>
      </LegalSection>
      <LegalSection title="Données personnelles">
        <p>Conformément à la réglementation applicable, vous disposez d'un droit d'accès, de rectification, d'effacement et d'opposition concernant vos données personnelles. Pour exercer ces droits : aurahomecaretahiti@gmail.com</p>
      </LegalSection>
    </div>
  );
}

function PrivacyContent() {
  return (
    <div className="space-y-2">
      <LegalSection title="Collecte des données">
        <p>Dans le cadre de la relation contractuelle, AŪRA HOME CARE collecte certaines données personnelles : nom, prénom, adresse, téléphone, email, informations relatives à la propriété et coordonnées de personnes à contacter en cas d'urgence.</p>
      </LegalSection>
      <LegalSection title="Finalité du traitement">
        <p>Les données sont collectées pour la gestion de la relation contractuelle, l'organisation des prestations, la communication avec le Client, la facturation et la coordination d'éventuels intervenants externes.</p>
      </LegalSection>
      <LegalSection title="Base légale">
        <p>Le traitement repose sur l'exécution du contrat et l'intérêt légitime d'AŪRA HOME CARE à assurer le bon fonctionnement de ses services.</p>
      </LegalSection>
      <LegalSection title="Destinataires">
        <p>Les données sont destinées exclusivement à AŪRA HOME CARE. Aucune donnée n'est vendue ni cédée à des tiers à des fins commerciales.</p>
      </LegalSection>
      <LegalSection title="Durée de conservation">
        <p>Les données sont conservées pendant la durée nécessaire à la gestion de la relation contractuelle et pour une durée raisonnable après la fin de la mission.</p>
      </LegalSection>
      <LegalSection title="Vos droits">
        <p>Vous disposez d'un droit d'accès, de rectification, d'effacement, d'opposition et de limitation du traitement. Pour exercer vos droits, adressez une demande à : aurahomecaretahiti@gmail.com</p>
      </LegalSection>
      <LegalSection title="Médiation">
        <p>Le médiateur compétent est : Association des Médiateurs Diplômés de Polynésie Française (AMDPF) — https://www.amdpf.com</p>
      </LegalSection>
    </div>
  );
}
